/**
 * FindMyMarble API Client
 * Fetches catalog data from FindMyMarble-AOD for the Emperor Marble Shopify storefront.
 * Exposes window.FindMyMarbleAPI with: getStoneTypes, getBlocks, getBlockDetail, getBundleSlabs.
 * All methods return { error: true, message } on failure (never throw).
 * Uses sessionStorage caching with 5-minute TTL.
 */
(function () {
  'use strict';

  var CACHE_TTL_MS = 5 * 60 * 1000;
  var FETCH_TIMEOUT_MS = 8000;
  var WRITE_TIMEOUT_MS = 12000;
  var CACHE_PREFIX = 'fmm_';
  var MEDIA_KEYS = ['images', 'videos', 'close_up_images', 'book_matched_images'];
  var NESTED_LIST_KEYS = ['blocks', 'bundles', 'slabs'];

  function FindMyMarbleClient(apiUrl) {
    this.apiUrl = apiUrl.replace(/\/+$/, '');
  }

  // -- sessionStorage helpers --------------------------------------------------

  FindMyMarbleClient.prototype._getCache = function (key) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      var fresh = Date.now() - entry.timestamp < CACHE_TTL_MS;
      return { data: entry.data, fresh: fresh };
    } catch (_e) {
      return null;
    }
  };

  FindMyMarbleClient.prototype._setCache = function (key, data) {
    try {
      sessionStorage.setItem(
        CACHE_PREFIX + key,
        JSON.stringify({ data: data, timestamp: Date.now() })
      );
    } catch (_e) { /* sessionStorage unavailable — continue without cache */ }
  };

  // -- Fetch with cache + error handling ---------------------------------------

  FindMyMarbleClient.prototype._cachedFetch = async function (cacheKey, url) {
    var cached = this._getCache(cacheKey);
    if (cached && cached.fresh) return cached.data;

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timeoutId = controller ? setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS) : null;

    try {
      var response = await fetch(url, controller ? { signal: controller.signal } : undefined);
      if (!response.ok) {
        throw new Error('HTTP ' + response.status + ': ' + response.statusText);
      }
      var data = await response.json();
      this._setCache(cacheKey, data);
      this._publishLoaded(data);
      return data;
    } catch (err) {
      var message = err && err.name === 'AbortError' ? 'Request timed out' : err.message;
      this._publishError(message, url);
      if (cached && cached.data) return cached.data;
      return { error: true, message: message };
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  };

  // -- Pub/Sub helpers (guard against missing globals) -------------------------

  FindMyMarbleClient.prototype._publishLoaded = function (data) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (!PUB_SUB_EVENTS.inventoryLoaded) return;
    var stoneTypes = data && data.stone_types;
    publish(PUB_SUB_EVENTS.inventoryLoaded, {
      stoneName: stoneTypes ? undefined : (data.stone_name || undefined),
      blockCount: stoneTypes ? stoneTypes.length : (data.count || 0),
    });
  };

  FindMyMarbleClient.prototype._publishError = function (message, endpoint) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (!PUB_SUB_EVENTS.inventoryError) return;
    publish(PUB_SUB_EVENTS.inventoryError, { message: message, endpoint: endpoint });
  };

  // -- Normalize media arrays (null/undefined -> []) ---------------------------

  FindMyMarbleClient.prototype._normalizeMedia = function (obj) {
    if (!obj || typeof obj !== 'object') return obj;
    var self = this;

    MEDIA_KEYS.forEach(function (key) {
      if (key in obj && !Array.isArray(obj[key])) obj[key] = [];
    });

    NESTED_LIST_KEYS.forEach(function (listKey) {
      if (Array.isArray(obj[listKey])) {
        obj[listKey] = obj[listKey].map(function (item) {
          return self._normalizeMedia(item);
        });
      }
    });

    if (obj.block) obj.block = this._normalizeMedia(obj.block);
    return obj;
  };

  // -- Public API methods ------------------------------------------------------

  /** @returns {Promise<{stone_types: Array} | {error: true, message: string}>} */
  FindMyMarbleClient.prototype.getStoneTypes = async function () {
    var data = await this._cachedFetch('stoneTypes', this.apiUrl + '/api/shopify/stone-types');
    return this._normalizeMedia(data);
  };

  /** @returns {Promise<{stone_types: Array, facets: Object, count: number} | {error: true, message: string}>} */
  FindMyMarbleClient.prototype.getCollectionData = async function () {
    var data = await this._cachedFetch('collectionData', this.apiUrl + '/api/shopify/collection-data');
    return this._normalizeMedia(data);
  };

  /** @returns {Promise<{blocks: Array} | {error: true, message: string}>} */
  FindMyMarbleClient.prototype.getBlocks = async function (stoneName) {
    if (!stoneName) return { error: true, message: 'stoneName parameter is required' };
    var url = this.apiUrl + '/api/shopify/blocks?stone_name=' + encodeURIComponent(stoneName);
    var data = await this._cachedFetch('blocks_' + stoneName, url);
    return this._normalizeMedia(data);
  };

  /** @returns {Promise<{block: Object} | {error: true, message: string}>} */
  FindMyMarbleClient.prototype.getBlockDetail = async function (blockId) {
    if (!blockId) return { error: true, message: 'blockId parameter is required' };
    var url = this.apiUrl + '/api/shopify/blocks/' + encodeURIComponent(blockId);
    var data = await this._cachedFetch('block_' + blockId, url);
    return this._normalizeMedia(data);
  };

  /** @returns {Promise<{slabs: Array} | {error: true, message: string}>} */
  FindMyMarbleClient.prototype.getBundleSlabs = async function (bundleId) {
    if (!bundleId) return { error: true, message: 'bundleId parameter is required' };
    var url = this.apiUrl + '/api/shopify/bundles/' + encodeURIComponent(bundleId) + '/slabs';
    var data = await this._cachedFetch('slabs_' + bundleId, url);
    return this._normalizeMedia(data);
  };

  /**
   * Submit a quote request.
   * POST requests are never cached.
   *
   * @param {Object} data - Quote request payload
   * @param {string} data.stone_name - Stone type name (required)
   * @param {string} data.block_id - Catalog block UUID (required)
   * @param {string} [data.block_code] - Block code for display
   * @param {string} [data.stone_category] - marble/granite/limestone/etc
   * @param {string} [data.bundle_id] - Catalog bundle UUID
   * @param {number} [data.bundle_number] - Bundle number
   * @param {string} [data.bundle_finish] - polished/honed/brushed/filled/tumbled
   * @param {number} data.quantity - Requested quantity
   * @param {string} data.unit - "sqm" or "piece"
   * @param {string} [data.product_dimensions] - e.g. "280x160x2cm"
   * @param {Object} data.customer - Customer information (required)
   * @param {string} data.customer.company_name - Company name (required)
   * @param {string} data.customer.email - Email address (required)
   * @param {string} [data.customer.phone] - Phone number
   * @param {string} [data.customer.customer_type] - fabricator/architect_designer/contractor
   * @param {string} [data.customer.notes] - Customer message
   * @returns {Promise<{success: boolean, reference_number: string} | {error: boolean, message: string}>}
   */
  FindMyMarbleClient.prototype.submitQuote = async function (data) {
    if (!data || !data.stone_name || !data.block_id || !data.customer) {
      return { error: true, message: 'Missing required fields' };
    }
    if (!data.customer.company_name || !data.customer.email) {
      return { error: true, message: 'Company name and email are required' };
    }

    try {
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timeoutId = controller ? setTimeout(function () { controller.abort(); }, WRITE_TIMEOUT_MS) : null;
      var response = await fetch(this.apiUrl + '/api/shopify/quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        signal: controller ? controller.signal : undefined
      });
      var result = await response.json();
      if (!response.ok || result.error) {
        return { error: true, message: result.message || 'Quote submission failed' };
      }
      return result;
    } catch (err) {
      return {
        error: true,
        message: err && err.name === 'AbortError' ? 'Request timed out' : err.message
      };
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  };

  // -- Initialization ----------------------------------------------------------

  function init() {
    var apiUrl = window.__FINDMYMARBLE_API_URL;
    if (!apiUrl) {
      console.warn('FindMyMarble: API URL not configured (window.__FINDMYMARBLE_API_URL is missing)');
      return;
    }
    window.FindMyMarbleAPI = new FindMyMarbleClient(apiUrl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
